/**
 * Fleet stats: every bonus in the game summed into one additive % per stat.
 * A stat's multiplier is (1 + value), so +25% from research and +10% from the
 * Matrix make ×1.35, never ×1.375 — one rule, easy to read on screen.
 */

import { FLEET_STAT_LABEL, fitBonus, fitLevel, resource, type FleetStat } from '../content/resources';
import { projectDef } from '../content/projects';
import { MATRIX_BY_ID } from '../content/matrix';
import { driftDef } from '../content/drifts';
import { ADDITIONS } from '../content/flags';
import type { GameState } from './state';

export type FleetStats = Record<FleetStat, number>;

const STAT_IDS = Object.keys(FLEET_STAT_LABEL) as FleetStat[];

export function fleetStats(state: GameState): FleetStats {
  const s = {} as FleetStats;
  for (const id of STAT_IDS) s[id] = 0;

  for (const id of ADDITIONS.fittings ? state.socketed : []) {
    const def = resource(id);
    const bonus = fitBonus(def.rarity!, fitLevel(state.fittings[id] ?? 0));
    for (const stat of def.fits ?? []) s[stat] += bonus;
  }

  for (const id of state.run.projects) {
    for (const effect of projectDef(id).effects) {
      if (effect.kind === 'stat') s[effect.stat] += effect.amount;
    }
  }

  for (const [id, level] of Object.entries(state.matrix)) {
    const node = MATRIX_BY_ID.get(id);
    // Crowns are one-off; every other node's effect is per level.
    const times = node?.kind === 'crown' ? Math.min(1, level) : level;
    for (const effect of node?.effects ?? []) s[effect.stat] += effect.amount * times;
  }

  const drift = ADDITIONS.drifts ? driftDef(state.run.drift) : null;
  if (drift?.stats) for (const [stat, amount] of Object.entries(drift.stats)) s[stat as FleetStat] += amount!;

  for (const id of STAT_IDS) s[id] = Math.max(-0.9, s[id]);
  return s;
}
